<template>
  <div>
    <div id="doctor-ai" class="fixed bottom-0 right-0 p-4">
      <div class="rounded-full w-16 h-16 bg-pri cursor-pointer shadow-lg drop-shadow-lg">
        <img class="w-full h-full" src="/assets/images/spd_lg.png" alt="SuperDoc" />
      </div>
    </div>
    <el-drawer :visible.sync="drawer" :direction="direction" size="40%" :wrapperClosable="false" @close="close">
      <template #title>
        <div class="flex items-center gap-2">
          <div class="overflow rounded-full cursor-pointer bg-pri shadow-lg">
            <img class="w-4 h-4" src="/assets/images/spd_lg.png" alt="SuperDoc" />
          </div>
          <h3 class="text-black font-bold fs-18 mb-0">SuperDoc</h3>
        </div>
      </template>
      <div class="flex flex-1 flex-col h-full border-t border-2 border-gray-200 px-3 pb-3">
        <div class="flex flex-1 py-1">
          <div class="h-full w-full overflow-auto">
            <!-- Info -->
            <div class="flex flex-col items-center justify-center mb-3">
              <div class="overflow rounded-full w-16 h-16 cursor-pointer bg-pri shadow-lg mb-2">
                <img class="w-full h-full" src="/assets/images/spd_lg.png" alt="SuperDoc" />
              </div>
              <h2 class="text-center">SuperDoc</h2>
              <p class="text-center text-sm text-gray-500">{{ $t('lbl_super_doc.lbl_title') }}</p>
            </div>
            <!--  -->
            <div class="flex" v-if="aiWelcomeMessage != ''">
              <div class="mw-80 mb-2 bg-gradient-to-b from-gray-100 to-gray-200 rounded-md overflow-hidden">
                <div class="d-inline-block border p-2 rounded-md markdown-content message-item"
                  v-html="compiledMarkdown(aiWelcomeMessage)">
                </div>
              </div>
            </div>
            <div v-if="processing" class="font-xs text-gray-600 text-xs flex items-center">
              <span>{{ $t('lbl_super_doc.lbl_initializing_msg') }}</span>
              <span class="ml-1 animate-dots">.</span>
            </div>
            <template v-for="(item, index) in messages" :index="index">
              <div class="flex justify-content-end">
                <div :ref="index === messages.length - 1 ? 'lastMessage' : null"
                  class="mw-80 mb-2 bg-gradient-to-b from-blue-100 to-blue-200 rounded-md overflow-hidden message-item">
                  <div class="d-inline-block border p-2 rounded-md markdown-content" v-html="item.question"></div>
                </div>
              </div>
              <div v-if="item.error">
                <div v-if="item.error && !reSending && index === messages.length - 1"
                  class="flex justify-content-end mb-2">
                  <div class="font-xs text-gray-600 text-xs flex items-center">
                    <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                      $t('lbl_super_doc.lbl_error_occurred_msg') }} <span class="underline cursor-pointer"
                        @click="reSendMessage(item.question, index)">{{ $t('lbl_super_doc.lbl_error_try_again_msg')
                        }}</span></span>
                  </div>
                </div>
                <div v-else class="flex justify-content-end mb-2">
                  <div class="font-xs text-gray-600 text-xs flex items-center">
                    <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                      $t('lbl_super_doc.lbl_error_occurred_msg') }}</span>
                  </div>
                </div>
              </div>
              <div v-if="thinking && index === messages.length - 1"
                class="font-xs text-gray-600 text-xs flex items-center">
                <span>{{ $t('lbl_super_doc.lbl_thinking_msg') }}</span>
                <span class="ml-1 animate-dots">.</span>
              </div>
              <div class="flex" v-if="item.answer !== ''">
                <div
                  class="mw-80 mb-2 bg-gradient-to-b from-gray-100 to-gray-200 rounded-md overflow-hidden message-item">
                  <div class="border p-2 rounded-md markdown-content" v-html="compiledMarkdown(item.answer)"></div>
                </div>
              </div>
            </template>
            <div v-if="findTime && deepThinkingTime">
              <small class="font-xs text-gray-400 text-xs">{{ $t('lbl_super_doc.lbl_performance_msg', {
                deep_thinking_time: appUtils.formatDurationMoment(deepThinkingTime, $lang)
              }) }}</small>
            </div>
          </div>
        </div>
        <div class="sticky bottom-0 left-0 right-0 mt-auto bg-white">
          <div class="flex items-center justify-center gap-2">
            <el-input v-model="question" type="textarea" :rows="3"
              :placeholder="$t('lbl_super_doc.lbl_input_placeholder')" :disabled="thinking"
              @keydown.enter.native.exact.prevent="sendMessage(question)"></el-input>
            <el-button type="primary" icon="el-icon-s-promotion" :disabled="thinking" circle
              @click="sendMessage(question)"></el-button>
          </div>
          <div><small class="font-xs text-gray-400 text-xs">{{ $t('lbl_super_doc.lbl_note_msg') }}</small></div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>
<script>
import axios from '@/api/axios'
import appUtils from '@/utils/appUtils'
import { getTypeConfig } from './doctorAIConfig'

export default {
  name: 'DoctorAI',
  props: {
    /** Nội dung câu hỏi mặc định (auto-send khi mở, nếu chưa có cache) */
    issue: String,
    /** Loại context: BENH_AN | HOI_CHAN | ... (xem doctorAIConfig.js) */
    type: {
      type: String,
      default: ''
    },
    /** HOI_CHAN: session ID */
    sessionId: [String, Number],
    /** HOI_CHAN: disease ID */
    diseaseId: [String, Number],
    /** BENH_AN: bệnh án ID (BADT) */
    badtId: [String, Number],
    hasResult: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      appUtils,
      question: '',
      messages: [],
      history: [],
      thinking: false,
      processing: false,
      drawer: false,
      direction: 'rtl',
      aiWelcomeMessage: '',
      reSending: false,
      cancelFn: null,
      findTime: null,
      deepThinkingTime: null
    }
  },
  computed: {
    typeConfig () {
      return getTypeConfig(this.type)
    },
    storageContextId () {
      return this.typeConfig.getContextId(this)
    }
  },
  watch: {
    storageContextId () {
      this.reloadCacheIfDrawerOpen()
    },
    hasResult: {
      handler (newVal) {
        if (newVal && this.drawer) {
          this.reloadCacheIfDrawerOpen()
        }
      },
      deep: true
    },
    hasError: {
      handler (newVal) {
        if (newVal && this.drawer) {
          this.messages.lastItem.error = true
          this.thinking = false
          this.processing = false
        }
      },
      deep: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      const chatButton = document.getElementById('doctor-ai')
      if (chatButton) {
        chatButton.addEventListener('click', () => {
          this.drawer = true
          this.onDrawerOpen()
        })
      }
    })
  },
  methods: {
    getStorageContextId () {
      return this.typeConfig.getContextId(this)
    },
    hasStorageContext () {
      return this.typeConfig.hasStorage(this)
    },
    getDoctorAIStorageKey () {
      return this.typeConfig.storageKey(this)
    },
    getDoctorAIStorageData () {
      try {
        const key = this.getDoctorAIStorageKey()
        const raw = window.localStorage.getItem(key)
        if (!raw || !appUtils.isJSON(raw)) {
          return { processingIds: [], rawData: {} }
        }
        const data = JSON.parse(raw)
        if (!data || typeof data !== 'object') {
          return { processingIds: [], rawData: {} }
        }
        return {
          processingIds: Array.isArray(data.processingIds) ? data.processingIds : [],
          rawData: data.rawData && typeof data.rawData === 'object' ? data.rawData : {}
        }
      } catch (e) {
        console.error('Error reading doctor AI storage data:', e)
        return { processingIds: [], rawData: {} }
      }
    },
    saveDoctorAIStorageData (processingIds, rawData) {
      const key = this.getDoctorAIStorageKey()
      const storageData = this.getDoctorAIStorageData()
      const updatedProcessingIds = processingIds || storageData.processingIds
      const updatedRawData = rawData || storageData.rawData
      window.localStorage.setItem(key, JSON.stringify({
        processingIds: updatedProcessingIds,
        rawData: updatedRawData
      }))
    },
    loadFromLocalStorage () {
      if (!this.hasStorageContext()) return null
      const storageData = this.getDoctorAIStorageData()
      const contextId = this.getStorageContextId()
      const contextData = contextId ? storageData.rawData[contextId] : null
      if (!contextData?.history?.length) return null
      return contextData.history.map(h => ({ question: h.question, answer: h.answer || '' }))
    },
    reloadCacheIfDrawerOpen () {
      if (!this.drawer || !this.hasStorageContext()) return
      this.messages = []
      this.history = []
      const cachedMessages = this.loadFromLocalStorage()
      if (cachedMessages?.length) {
        this.messages = cachedMessages
        this.history = cachedMessages.map(m => ({ question: m.question, answer: m.answer }))
        this.thinking = false
        this.processing = false
        this.scrollToLastMessage()
      }
    },
    onDrawerOpen () {
      this.processing = true
      setTimeout(() => {
        if (!this.issue) {
          this.aiWelcomeMessage = this.typeConfig.getWelcomeMessageNoIssue(this)
        } else {
          this.aiWelcomeMessage = this.$t('lbl_super_doc.lbl_welcome_msg')
          const cachedMessages = this.loadFromLocalStorage()
          if (cachedMessages && cachedMessages.length > 0) {
            this.messages = cachedMessages
            this.history = cachedMessages.map(m => ({ question: m.question, answer: m.answer }))
            this.scrollToLastMessage()
          } else if (this.messages.length === 0) {
            this.sendMessage(this.issue)
          }
        }
        this.processing = false
      }, 500)
    },
    async reSendMessage (question, index) {
      if (!question) return

      let self = this
      self.thinking = true
      self.reSending = true
      self.findTime = null
      self.deepThinkingTime = null

      let body = {
        lang: self.$lang,
        question: question,
        history: self.history
      }
      await self.$rf
        .getRequest('DoctorRequest')
        .doctorAiAsk(body)
        .then((res) => {
          if (res.status === 200) {
            self.question = ''
            delete self.messages[index].error
            self.messages[index].answer = res.data.answer
            self.thinking = false
            self.findTime = res.data.find_time
            self.deepThinkingTime = res.data.deep_thinking_time
            self.history.push(self.messages[index])
            self.scrollToLastMessage()
            self.saveToLocalStorage(question, res.data.answer)
          }
        })
        .catch(e => {
          console.log(e)
          self.messages[index].error = true
        })
        .finally(() => {
          self.thinking = false
          self.reSending = false
        })
    },
    saveToLocalStorage (question, answer) {
      if (!this.hasStorageContext()) return
      const contextId = this.getStorageContextId()
      if (!contextId) return
      const storageData = this.getDoctorAIStorageData()
      // Xóa khỏi processingIds khi đã có answer
      const updatedProcessingIds = storageData.processingIds.filter(id => id !== contextId)
      const newHistoryItem = { question, answer }
      const updatedHistory = [...(storageData.rawData[contextId]?.history || []), newHistoryItem]
      const updatedRawData = { ...storageData.rawData }
      updatedRawData[contextId] = {
        id: contextId,
        question,
        answer,
        history: updatedHistory
      }
      this.saveDoctorAIStorageData(updatedProcessingIds, updatedRawData)
    },
    async sendMessage (question) {
      if (!question) return

      const self = this
      const contextId = self.storageContextId
      // hasStorageContext kiểm tra đủ điều kiện (vd: HOI_CHAN cần cả sessionId lẫn diseaseId)
      const hasStorage = self.hasStorageContext()

      if (hasStorage && contextId) {
        const storageData = self.getDoctorAIStorageData()
        const existingContext = storageData.rawData[contextId]

        // Đang xử lý cho ID này và chưa có answer → không gửi thêm (chỉ áp dụng cho auto-send lần đầu)
        if (storageData.processingIds.includes(contextId) && !existingContext?.answer && self.messages.length === 0) {
          self.thinking = true
          self.processing = true
          self.findTime = null
          self.deepThinkingTime = null
          self.messages.push({ question, answer: '' })
          return
        }
        // Đã có answer và chưa có messages → không gửi lại (đã được load từ cache)
        if (existingContext?.answer && self.messages.length === 0) return

        if (!storageData.processingIds.includes(contextId)) {
          storageData.processingIds.push(contextId)
        }
        const contextHistory = existingContext?.history || []
        storageData.rawData[contextId] = { id: contextId, question, history: contextHistory }
        self.saveDoctorAIStorageData(storageData.processingIds, storageData.rawData)
      }

      self.thinking = true
      self.findTime = null
      self.deepThinkingTime = null
      self.question = ''
      self.messages.push({ question, answer: '' })

      let body = {
        lang: self.$lang,
        question: question,
        history: self.history
      }
      const source = axios.CancelToken.source()
      self.cancelFn = source.cancel
      let configs = {
        cancelToken: source.token
      }
      await self.$rf
        .getRequest('DoctorRequest')
        .doctorAiAsk(body, configs)
        .then((res) => {
          if (res.status === 200) {
            delete self.messages.lastItem.error
            self.messages.lastItem.answer = res.data.answer
            self.findTime = res.data.find_time
            self.deepThinkingTime = res.data.deep_thinking_time
            self.thinking = false
            self.history.push(self.messages.lastItem)
            self.scrollToLastMessage()
            self.cancelFn = null
            self.saveToLocalStorage(question, res.data.answer)
          }
        })
        .catch(e => {
          console.log(e)
          self.messages.lastItem.error = true
          if (hasStorage && contextId) {
            const storageData = self.getDoctorAIStorageData()
            const idx = storageData.processingIds.indexOf(contextId)
            if (idx > -1) storageData.processingIds.splice(idx, 1)
            self.saveDoctorAIStorageData(storageData.processingIds, storageData.rawData)
          }
          if (axios.isCancel(e)) {
            console.log('Request canceled', e.message)
          } else {
            self.$message.error('An error occurred. Please try again later.')
          }
        })
        .finally(() => {
          self.thinking = false
        })
    },
    cancelRequest () {
      if (this.cancelFn) {
        this.cancelFn('Request canceled by the user.')
        this.cancelFn = null
      }
    },
    compiledMarkdown (message) {
      return appUtils.stripMarkdownKeepHTML(message)
      // const markdownText = message.replace(/\\n/g, '\n')
      // marked.setOptions({
      //   breaks: false
      // })
      // return marked(markdownText)
    },
    scrollToLastMessage () {
      this.$nextTick(() => {
        const last = this.$refs.lastMessage
        if (last && last[0]) {
          last[0].scrollIntoView({ behavior: 'auto', block: 'start' })
        }
      })
    },
    close () {
      this.thinking = false
      this.aiWelcomeMessage = ''
    }
  },
  beforeDestroy () {
    this.cancelRequest()
  }
}
</script>
<style scoped>
.mw-80 {
  max-width: 80%;
}

@keyframes dots {
  0% {
    content: '';
  }

  25% {
    content: '.';
  }

  50% {
    content: '..';
  }

  75% {
    content: '...';
  }

  100% {
    content: '';
  }
}

.animate-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

.message-item {
  opacity: 0;
  transform: translateY(10px);
  animation: fadeSlide 0.4s ease forwards;
}

@keyframes fadeSlide {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
